import { CustomDataGrid } from "@app/components";
import { Box, Grid, TextField, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { GridColDef } from "@mui/x-data-grid";

enum TABLE_HUMIDITY_LABELS {
  CAPSULE_TARA = "Tara cápsula (g)",
  WET_SAMPLE_CAPSULE = "Peso húmedo muestra + cápsula (g)",
  WET_SAMPLE = "Peso húmedo muestra (g)",
}

interface AnalysisFormProps {
  count: number;
  setHumidityValue: (value: number) => void;
}

const TableHumidityForm = ({ count, setHumidityValue }: AnalysisFormProps) => {
  const { watch, setValue } = useFormContext();
  const inputRef = useRef<{ [key: string]: HTMLInputElement | null }>({});

  let rowsCurvaDrying = watch("rowsCurvaDryingCalcination");
  if (!rowsCurvaDrying || !Array.isArray(rowsCurvaDrying)) {
    rowsCurvaDrying = [];
  }

  useEffect(() => {
    if (!rowsCurvaDrying || rowsCurvaDrying.length === 0) {
      const initialRows = generateInitialRows(count);
      setValue("rowsCurvaDryingCalcination", initialRows);
    }
  }, [rowsCurvaDrying, setValue, count]);

  const generateInitialRows = (count: number) => {
    return [
      {
        id: "drying_1",
        label: TABLE_HUMIDITY_LABELS.CAPSULE_TARA,
        ...Object.fromEntries(
          Array.from({ length: count }, (_, i) => [`capsule_dry_${i + 1}`, ""])
        ),
        total: 0,
      },
      {
        id: "drying_2",
        label: TABLE_HUMIDITY_LABELS.WET_SAMPLE_CAPSULE,
        ...Object.fromEntries(
          Array.from({ length: count }, (_, i) => [`capsule_dry_${i + 1}`, ""])
        ),
        total: 0,
      },
      {
        id: "drying_3",
        label: TABLE_HUMIDITY_LABELS.WET_SAMPLE,
        ...Object.fromEntries(
          Array.from({ length: count }, (_, i) => [`capsule_dry_${i + 1}`, ""])
        ),
        total: 0,
      },
    ];
  };

  const columnsDryingCurva: GridColDef[] = useMemo(() => {
    const dynamicColumns: GridColDef[] = [
      {
        field: "label",
        headerName: "Pesos Húmedos",
        headerAlign: "center",
        align: "left",
        flex: 1,
        editable: false,
      },
    ];

    for (let i = 1; i <= count; i++) {
      dynamicColumns.push({
        field: `capsule_dry_${i}`,
        headerName: `Cápsula ${i}`,
        headerAlign: "center",
        align: "center",
        editable: true,
        flex: 1,
        renderCell: (params) => {
          return (
            <Box
              sx={{
                width: "100%",
                display: "flex",
              }}
            >
              <TextField
                inputRef={(el) =>
                  (inputRef.current[`${params.id}_${params.field}`] = el)
                }
                type="text"
                value={params.value}
                disabled={params.row.label === TABLE_HUMIDITY_LABELS.WET_SAMPLE}
                onChange={(e) => {
                  if (params.row.label !== TABLE_HUMIDITY_LABELS.WET_SAMPLE) {
                    setValue(
                      `rowsCurvaDryingCalcination.${params.id}.${params.field}`,
                      e.target.value
                    );
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Tab") {
                    e.preventDefault();

                    const keys = Object.keys(inputRef.current);
                    const currentIndex = keys.indexOf(
                      `${params.id}_${params.field}`
                    );

                    if (currentIndex !== -1) {
                      let nextIndex = currentIndex + 1;
                      while (
                        nextIndex < keys.length &&
                        inputRef.current[keys[nextIndex]]?.disabled
                      ) {
                        nextIndex++;
                      }

                      const nextInputKey = keys[nextIndex];

                      if (nextInputKey && inputRef.current[nextInputKey]) {
                        setTimeout(
                          () => inputRef.current[nextInputKey]?.focus(),
                          0
                        );
                      }
                    }
                  }
                }}
                fullWidth
                sx={{
                  "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                    boxSizing: "inherit",
                  },
                }}
              />
            </Box>
          );
        },
      });
    }

    dynamicColumns.push({
      field: "total",
      headerName: "Total",
      headerAlign: "center",
      align: "center",
      editable: false,
      flex: 1,
      renderCell: (params) => (
        <div style={{ fontWeight: "bold" }}>
          {Number(params.row.total || 0).toFixed(2)}
        </div>
      ),
    });

    return dynamicColumns;
  }, [count]);

  const processRowUpdate = (newRow) => {
    let updatedRows = rowsCurvaDrying.map((row) => ({ ...row }));

    if (
      newRow.label === TABLE_HUMIDITY_LABELS.CAPSULE_TARA ||
      newRow.label === TABLE_HUMIDITY_LABELS.WET_SAMPLE_CAPSULE
    ) {
      let total = 0;

      for (let i = 1; i <= count; i++) {
        const capsuleValue = Number(newRow[`capsule_dry_${i}`]) || 0;
        total += capsuleValue;
      }

      newRow.total = total.toFixed(2);
    }

    updatedRows = updatedRows.map((row) =>
      row.id === newRow.id ? newRow : row
    );

    const taraRow = updatedRows.find(
      (row) => row.label === TABLE_HUMIDITY_LABELS.CAPSULE_TARA
    );
    const pesoTotalRow = updatedRows.find(
      (row) => row.label === TABLE_HUMIDITY_LABELS.WET_SAMPLE_CAPSULE
    );
    const pesoMuestraRow = updatedRows.find(
      (row) => row.label === TABLE_HUMIDITY_LABELS.WET_SAMPLE
    );

    if (taraRow && pesoTotalRow && pesoMuestraRow) {
      let totalPesoCalc = 0;

      for (let i = 1; i <= count; i++) {
        const pesoTotal = Number(pesoTotalRow[`capsule_dry_${i}`]) || 0;
        const pesoTara = Number(taraRow[`capsule_dry_${i}`]) || 0;
        const pesoMuestra = (pesoTotal - pesoTara).toFixed(2);

        pesoMuestraRow[`capsule_dry_${i}`] = pesoMuestra;
        totalPesoCalc += Number(pesoMuestra);
      }

      pesoMuestraRow.total = totalPesoCalc.toFixed(2);
    }

    setValue("rowsCurvaDryingCalcination", [...updatedRows]);

    setTimeout(() => {
      const keys = Object.keys(inputRef.current);

      const currentFilter = Object.keys(newRow).filter((key) =>
        key.startsWith("capsule_dry_")
      );

      const firstEmptyInput = currentFilter.find((key) => newRow[key] === "");

      if (firstEmptyInput) {
        const firstEmptyInputKey = keys.find(
          (key) => key === `${newRow.id}_${firstEmptyInput}`
        );

        if (firstEmptyInputKey && inputRef.current[firstEmptyInputKey]) {
          setTimeout(() => inputRef.current[firstEmptyInputKey]?.focus(), 0);
        }
      } else if (newRow.label === TABLE_HUMIDITY_LABELS.CAPSULE_TARA) {
        const nextRowId = "drying_2";
        const firstInputKey = `${nextRowId}_capsule_dry_1`;

        if (inputRef.current[firstInputKey]) {
          setTimeout(() => inputRef.current[firstInputKey]?.focus(), 0);
        }
      }
    }, 0);

    return newRow;
  };

  const totalPesoCalcinadoMuestra = useMemo(() => {
    const row = rowsCurvaDrying.find(
      (row) => row.label === TABLE_HUMIDITY_LABELS.WET_SAMPLE
    );
    return row ? Number(row.total) : 0;
  }, [rowsCurvaDrying]);

  useEffect(() => {
    setHumidityValue(totalPesoCalcinadoMuestra);
  }, [totalPesoCalcinadoMuestra, setHumidityValue]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CustomDataGrid
              key={`drying-${JSON.stringify(rowsCurvaDrying)}`}
              rows={rowsCurvaDrying || []}
              columns={columnsDryingCurva}
              search={false}
              processRowUpdate={processRowUpdate}
              disablePagination={true}
              footerContent={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    fontWeight: "bold",
                    gap: 2,
                  }}
                >
                  <Typography variant="sectionTitle">
                    Peso húmedo todas las muestras (g):
                  </Typography>
                  <Typography variant="sectionTitle">
                    {totalPesoCalcinadoMuestra.toFixed(2)}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TableHumidityForm;
